import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  clearWorkflowLimit,
  clearWorkspaceLimit,
  setDefaultLimit,
  setWorkflowLimit,
  setWorkspaceLimit,
} from '@/lib/api/generated/api';
import type { PeriodType } from '@/lib/api/generated/model';
import { UsageError } from '@/features/workspace-usage';

import { organizationKeys } from '../queryKeys';

/*
 * Changing what workspaces may spend (spec 073 US2).
 *
 * Three mutations, one invalidation rule: any limit change alters the *applied* limit
 * of workspaces the caller did not name — raising the default releases every workspace
 * that has no override, and that is the feature, not a side effect. So none of these
 * writes a row back into the cache. They discard the whole organization read and take
 * the service's answer, which is the only thing that knows which workspaces resolved to
 * what.
 */

/**
 * Shared failure handling: a refusal is explained, anything else is a failure.
 *
 * `subject` names what the 404 is about. The three tiers share every other message, but
 * a sentence with no subject — "That no longer exists" — reads as a bug on whichever
 * path it appears, and the workspace paths were already shipped saying "That workspace".
 */
function limitError(
  status: number,
  subject: 'workspace' | 'workflow' = 'workspace',
): UsageError {
  if (status === 403) {
    return new UsageError(
      'forbidden',
      'Only an organization administrator can change spend limits.',
    );
  }
  if (status === 404) {
    return new UsageError(
      'unavailable',
      `That ${subject} no longer exists, so no limit could be set for it.`,
    );
  }
  if (status === 422) {
    // The service validates the amount too, and its answer is the authoritative one.
    // Worded as a refusal of the figure rather than as a failure, because that is what
    // it is — the request arrived and was understood.
    return new UsageError(
      'unavailable',
      'That amount was refused. A limit must be more than zero, with at most two decimal places.',
    );
  }
  return new UsageError('unavailable', 'Could not save the limit.');
}

/** Everything the console changes invalidates the same read. */
function useInvalidateOrganization() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: organizationKeys.all });
}

export function useSetDefaultLimit() {
  const invalidate = useInvalidateOrganization();

  return useMutation({
    mutationFn: async ({
      amount,
      periodType,
    }: {
      amount: string;
      periodType: PeriodType;
    }) => {
      const response = await setDefaultLimit({
        amount,
        period_type: periodType,
      });
      if (response.status === 200) return response.data;
      throw limitError(response.status);
    },
    onSuccess: invalidate,
  });
}

export function useSetWorkspaceLimit() {
  const invalidate = useInvalidateOrganization();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      amount,
      periodType,
    }: {
      workspaceId: string;
      amount: string;
      periodType: PeriodType;
    }) => {
      const response = await setWorkspaceLimit(workspaceId, {
        amount,
        period_type: periodType,
      });
      if (response.status === 200) return response.data;
      throw limitError(response.status);
    },
    onSuccess: invalidate,
  });
}

/**
 * Cap one workflow (spec 080 US1).
 *
 * Invalidates the same read as the three above, deliberately. A workflow limit inherits
 * nothing and cannot change another workflow's applied cap — but it does change the
 * console's own table, and one invalidation rule for every limit change beats two that
 * have to be kept in step.
 *
 * The response carries `blocks_workflows` and `exceeds_workspace_limit` and nothing here
 * reads them: by the time they arrive the limit is already applied, and the warning they
 * describe belongs *before* the save. The dialog computes both from data already on the
 * page (`blockedForecast.ts`).
 */
export function useSetWorkflowLimit() {
  const invalidate = useInvalidateOrganization();

  return useMutation({
    mutationFn: async ({
      workflowId,
      amount,
      periodType,
    }: {
      workflowId: string;
      amount: string;
      periodType: PeriodType;
    }) => {
      const response = await setWorkflowLimit(workflowId, {
        amount,
        period_type: periodType,
      });
      if (response.status === 200) return response.data;
      throw limitError(response.status, 'workflow');
    },
    onSuccess: invalidate,
  });
}

export function useClearWorkflowLimit() {
  const invalidate = useInvalidateOrganization();

  return useMutation({
    mutationFn: async (workflowId: string) => {
      const response = await clearWorkflowLimit(workflowId);
      // 204: the cap is gone, and the workflow is bounded only by its workspace again.
      // Absence means no workflow cap — there is nothing for it to fall back to.
      if (response.status === 204) return;
      throw limitError(response.status, 'workflow');
    },
    onSuccess: invalidate,
  });
}

export function useClearWorkspaceLimit() {
  const invalidate = useInvalidateOrganization();

  return useMutation({
    mutationFn: async (workspaceId: string) => {
      const response = await clearWorkspaceLimit(workspaceId);
      // 204: the override is gone and there is nothing to return. The workspace's
      // applied limit is now the default, which the invalidated read will report.
      if (response.status === 204) return;
      throw limitError(response.status);
    },
    onSuccess: invalidate,
  });
}
